import { UserData } from '../../services/userService';
import { isAdminTier, isStaffTier } from '../../utils/roles';
import { ActionStatus, CustomerHealthDetail, Deal, DealStatus, HealthProduct } from './types';

/**
 * Pure formatting / derivation helpers for Customer Health. No JSX, no data fetching.
 */

/* ---------- money / percent ---------- */
export const money = (n: number) => '$' + Number(n || 0).toLocaleString();
/** Percent of n over d, guarded against a zero/absent denominator. */
export const pctOf = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

/* ---------- dates ---------- */
/** date-only ('YYYY-MM-DD') -> 'Jul 2' — parsed as local midnight so it never shifts a day. */
export const shortDate = (d: string) => new Date(d + 'T00:00:00').toLocaleString('en-US', { month: 'short', day: 'numeric' });
/** timestamptz ISO string -> 'Jul 2, 2026 · 9:15 AM'. */
export const shortTimestamp = (ts: string) => {
  const d = new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' + d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' });
};

/** Whether an action item is past its TAT (open items only). Evaluated client-side (local TZ);
 * the portfolio row/summary breach counts come from the view (UTC) and can differ by up to a day
 * at boundaries. */
export const isActionBreached = (openedOn: string, tatDays: number, status: ActionStatus) => {
  if (status === 'Done') return false;
  const due = new Date(openedOn + 'T00:00:00');
  due.setDate(due.getDate() + tatDays);
  return due.getTime() < Date.now();
};

/* ---------- people ---------- */
export const initialsOf = (name: string) => {
  const clean = (name || '').replace(/^[^,]*,\s*/, '').replace(/[^A-Za-z. ]/g, '');
  const parts = clean.replace(/\./g, ' ').split(/\s+/).filter(Boolean);
  return ((parts[0] || '?')[0] + ((parts[parts.length - 1] || '')[0] || '')).toUpperCase();
};

/** Resolve a stored user id to a display name, falling back to the raw value (covers legacy
 * free-text names from before the id migration). */
export const userDisplayName = (byId: Map<string, UserData>, idOrLegacy: string) =>
  byId.get(idOrLegacy)?.name ?? idOrLegacy;

/* ---------- roles ---------- */
/** Add/remove parents from the tracked portfolio — top 2 hierarchy tiers only (administrator/
 * executive in the current seed), mirroring the customer_health / user_assignment insert
 * permissions. Takes the current user's role_hierarchy, not a role name. */
export const canManageChPortfolio = (hierarchy?: number | null) => isAdminTier(hierarchy);
/** Edit an already-tracked parent's record — any staff tier (also business_dev/customer_success,
 * scoped server-side to their assignments). */
export const canEditChRecords = (hierarchy?: number | null) => isStaffTier(hierarchy);

/* ---------- deals ---------- */
export const isOpenDeal = (status: DealStatus) => status !== 'Closed Won' && status !== 'Closed Lost';
export const openDealsOf = (detail: CustomerHealthDetail): Deal[] => detail.deals.filter((d) => isOpenDeal(d.status));

/* ---------- products & efficacy ---------- */
/** A product card in the Products & Solution Efficacy section: an installed product overlaid with
 * its saved health record. `tracked` is false until a `customer_health_product` row exists — those
 * render as editable cards with no metrics yet. `installedOn` is carried so the card can show it. */
export interface DisplayProduct extends HealthProduct {
  installedOn?: string;
  tracked: boolean;
}

/**
 * The products to show for a parent: every product physically installed at its sites, overlaid with
 * its saved health record (status/metrics) where one exists, plus any tracked health product that
 * has no matching install (so nothing already recorded ever disappears). Metrics live in the health
 * record's jsonb, so an install with no record yet is simply an editable card with no metrics —
 * editing it upserts the first record. */
export const displayProductsFor = (detail: CustomerHealthDetail): DisplayProduct[] => {
  const byKey = new Map(detail.products.map((p) => [p.key, p]));
  const merged: DisplayProduct[] = detail.installedProducts.map((ip) => {
    const rec = byKey.get(ip.key);
    return rec
      ? { ...rec, name: rec.name || ip.name, installedOn: ip.installedOn, tracked: true }
      : { key: ip.key, name: ip.name, status: 'watch', threshold: '—', metrics: [], installedOn: ip.installedOn, tracked: false };
  });
  const installedKeys = new Set(detail.installedProducts.map((ip) => ip.key));
  for (const p of detail.products) if (!installedKeys.has(p.key)) merged.push({ ...p, tracked: true });
  return merged;
};
